import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { parseSort } from '@/utils/functions';

/**
 * Builders that translate the Back Office list filter state into the `filters`
 * snapshot sent to POST /exports.
 *
 * The export worker (Laravel) reproduces the list result set by running the
 * stored `filters` through the *same* list query builders (spec Open Q1 =
 * option A). So these builders must produce the same filter params the list
 * endpoints receive — minus list-presentation-only concerns (pagination,
 * eager-loading `with`, column/extra-field hints), which the backend resolves
 * itself or which do not affect the matched record set.
 *
 * They mirror the transforms in `models/lead.ts` (fetchLeadsEffect) and
 * `models/serviceRequest.ts` (fetchAppointmentList). If those change, update
 * these too.
 */

type RawFilters = Record<string, any>;

/**
 * Build the `filters` payload for a LEADS export from the table-view filter
 * state. Mirrors the TABLE branch of `fetchLeadsEffect`.
 */
export function buildLeadExportFilters(
  filters: RawFilters,
  options: { dateFilterType?: string } = {},
): Record<string, unknown> {
  const filteredObj: RawFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value !== null),
  );

  if (filteredObj.sort) {
    const { sort_by, sort_direction } = parseSort(filteredObj.sort);
    if (sort_by && sort_direction) {
      delete filteredObj.sort;
      filteredObj.sort_by = sort_by;
      filteredObj.sort_direction = sort_direction;
    }
  }

  filteredObj.is_archived = filteredObj.is_archived ? '1' : '0';

  if (!isEmpty(filteredObj.date)) {
    const dateFilterType = options.dateFilterType || 'created';
    filteredObj[`${dateFilterType}_from`] = dayjs(filteredObj.date?.start).format(
      'YYYY-MM-DD',
    );
    filteredObj[`${dateFilterType}_to`] = dayjs(filteredObj.date?.end).format(
      'YYYY-MM-DD',
    );
  }
  delete filteredObj.date;

  // List-presentation-only keys — not part of the matched record set.
  delete filteredObj.table_group_by;
  delete filteredObj.group_by;
  delete filteredObj.groups_to_be_loaded;
  delete filteredObj.page_size;

  return filteredObj;
}

export const APPOINTMENT_DATE_FILTER_TYPES = {
  APPOINTMENT: 'last_appointment',
  CREATED: 'created',
} as const;

export const DEFAULT_APPOINTMENT_DATE_FILTER_TYPE = APPOINTMENT_DATE_FILTER_TYPES.APPOINTMENT;

/**
 * Translate the appointments table `date` range into the service-request list
 * params: the appointment date filters on `start_date`/`end_date`
 * (utc_start_time/utc_end_time), the creation date on `created_from`/`created_to`.
 * Mutates and returns `filteredObj`; `date` is always removed.
 */
export function applyAppointmentDateFilter(
  filteredObj: RawFilters,
  dateFilterType: string = DEFAULT_APPOINTMENT_DATE_FILTER_TYPE,
): RawFilters {
  if (!isEmpty(filteredObj.date)) {
    const [fromKey, toKey] =
      dateFilterType === APPOINTMENT_DATE_FILTER_TYPES.CREATED
        ? ['created_from', 'created_to']
        : ['start_date', 'end_date'];
    if (filteredObj.date?.start)
      filteredObj[fromKey] = dayjs(filteredObj.date.start).format('YYYY-MM-DD');
    if (filteredObj.date?.end)
      filteredObj[toKey] = dayjs(filteredObj.date.end).format('YYYY-MM-DD');
  }
  delete filteredObj.date;
  return filteredObj;
}

/**
 * Build the `filters` payload for an APPOINTMENTS export from the appointments
 * table filter state. Mirrors `fetchAppointmentList`.
 */
export function buildAppointmentExportFilters(
  filters: RawFilters,
  options: { dateFilterType?: string } = {},
): Record<string, unknown> {
  const filteredObj: RawFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value !== null),
  );

  if (!filteredObj.showInactiveEvents) {
    filteredObj.status = 'confirmed,tentative';
  }
  delete filteredObj.showInactiveEvents;

  filteredObj.sort_by = 'utc_start_time';
  filteredObj.sort_direction = 'desc';

  applyAppointmentDateFilter(filteredObj, options.dateFilterType);

  // List-presentation-only keys.
  delete filteredObj.page_size;

  return filteredObj;
}

/**
 * Map the lead list's shown *property keys* (the table column identifiers, e.g.
 * `name`, `createdAt`, `leadSource`, `customerCreatedAt`) onto the LeadsExport's
 * backend column names (`firstname`/`lastname`, `created`, `lead_source`,
 * `customer_created`). Without this the two namespaces don't line up and the
 * backend's `resolveColumns` intersection silently drops most selected columns
 * (e.g. `name` never matched anything).
 *
 * Notes:
 * - The table's `status` property is the lead *state* (`LeadState`), so it maps
 *   to the backend `lead_state` column — NOT the customer `status` column.
 * - `name` expands to two CSV columns: `firstname` and `lastname`.
 * - Dynamic `extraField:<uuid>` columns are passed through verbatim; the backend
 *   resolves their values + header label against the partner's field types.
 */
// Keys mirror `LEAD_PROPERTIES` (Kanban/VisibilitySettings/constants). They are
// inlined as literals rather than imported to keep this util free of the heavy
// component dependency graph that import pulls in (it transitively loads the umi
// app entry, which breaks isolated unit tests). Keep in sync with LEAD_PROPERTIES.
const LEAD_EXPORT_COLUMN_MAP: Record<string, string[]> = {
  uuid: ['uuid'],
  name: ['firstname', 'lastname'],
  email: ['email'],
  phone: ['phone'],
  assignee: ['assignee'],
  status: ['lead_state'],
  priority: ['priority'],
  leadSource: ['lead_source'],
  marketingChannel: ['utm_medium'],
  marketingSource: ['utm_source'],
  marketingCampaign: ['utm_campaign'],
  dealSize: ['order_volume'],
  createdAt: ['created'],
  updatedAt: ['modified'],
  locale: ['locale'],
  note: ['notes'],
  title: ['title'],
  appointmentAt: ['latest_appointment'],
  appointmentBranch: ['latest_appointment_branch'],
  appointmentBranchService: ['latest_appointment_branch_service'],
  appointmentServiceType: ['latest_appointment_service_type'],
  customerCreatedAt: ['customer_created'],
  customerModifiedAt: ['customer_modified'],
  product_group: ['product_group'],
};

/** Dynamic extra-field column key, e.g. `extraField:<type-uuid>`. */
const EXTRA_FIELD_PREFIX = 'extraField:';

export function mapLeadExportColumns(shownProperties: string[]): string[] {
  const mapped = (shownProperties || [])
    .filter(Boolean)
    .flatMap((property) =>
      property.startsWith(EXTRA_FIELD_PREFIX)
        ? [property]
        : LEAD_EXPORT_COLUMN_MAP[property] || [],
    );

  return Array.from(new Set(mapped));
}

/**
 * Map the appointment list's shown *property keys* (`APPOINTMENT_PROPERTIES`,
 * e.g. `appointment:time`, `appointment:customerCreatedAt`) onto the
 * AppointmentsExport's backend column names. Without this the two namespaces
 * don't line up: none of the `appointment:*` keys match the backend's allowable
 * set, so every selected column was dropped and the export silently fell back
 * to the type's full default column set.
 *
 * Notes:
 * - `appointment:time` expands to the start AND end timestamp columns (the
 *   table renders both inside one cell).
 * - `appointment:lead` (the "Customer Project" cell) expands to the customer's
 *   name plus email/phone — the contact details an appointment export is for,
 *   which the table itself only shows in the row's detail drawer.
 * - `appointment:title` mirrors the table's fallback (the service type name
 *   when the appointment has no explicit title); the backend does the same.
 * - Dynamic `extraField:<uuid>` columns are passed through verbatim.
 */
// Keys mirror `APPOINTMENT_PROPERTIES` (Kanban/VisibilitySettings/constants),
// inlined as literals for the same reason as LEAD_EXPORT_COLUMN_MAP above.
// Keep in sync with APPOINTMENT_PROPERTIES and with the backend's
// AppointmentsExport::allowableColumns().
const APPOINTMENT_EXPORT_COLUMN_MAP: Record<string, string[]> = {
  'appointment:time': ['utc_start_time', 'utc_end_time'],
  'appointment:title': ['title'],
  'appointment:branch': ['branch'],
  'appointment:lead': ['firstname', 'lastname', 'email', 'phone'],
  'appointment:lead_source': ['lead_source'],
  'appointment:agent': ['assignee'],
  'appointment:status': ['status'],
  'appointment:serviceType': ['service_type'],
  'appointment:createdAt': ['created'],
  'appointment:updatedAt': ['modified'],
  'appointment:projectCreatedAt': ['lead_created'],
  'appointment:projectUpdatedAt': ['lead_modified'],
  'appointment:customerCreatedAt': ['customer_created'],
  'appointment:customerModifiedAt': ['customer_modified'],
  'appointment:attendanceStatus': ['attendance_status'],
};

export function mapAppointmentExportColumns(shownProperties: string[]): string[] {
  const mapped = (shownProperties || [])
    .filter(Boolean)
    .flatMap((property) =>
      property.startsWith(EXTRA_FIELD_PREFIX)
        ? [property]
        : APPOINTMENT_EXPORT_COLUMN_MAP[property] || [],
    );

  return Array.from(new Set(mapped));
}

export type ExportTypeKey = 'leads' | 'appointments';

/**
 * Compose the full POST /exports payload for the given list type, applying the
 * type-specific filter builder and resolving the columns. Pure + testable; the
 * `ExportCsvButton` component is a thin wrapper that feeds it Redux state.
 */
export function buildExportPayload(
  type: ExportTypeKey,
  filters: RawFilters,
  shownProperties: string[],
  options: { dateFilterType?: string } = {},
): { type: ExportTypeKey; filters: Record<string, unknown>; columns: string[] } {
  const exportFilters =
    type === 'leads'
      ? buildLeadExportFilters(filters, options)
      : buildAppointmentExportFilters(filters, options);

  const columns =
    type === 'leads'
      ? mapLeadExportColumns(shownProperties)
      : mapAppointmentExportColumns(shownProperties);

  return {
    type,
    filters: exportFilters,
    columns,
  };
}
